import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { paymentsAPI } from '../lib/api';
import { useTheme } from '../App';

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollStatus(0);
    }
  }, [sessionId]);

  const pollStatus = async (attempts) => {
    if (attempts >= 5) {
      setStatus('timeout');
      return;
    }

    try {
      const res = await paymentsAPI.status(sessionId);
      setPaymentData(res.data);

      if (res.data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (res.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setTimeout(() => pollStatus(attempts + 1), 2000);
    } catch {
      setTimeout(() => pollStatus(attempts + 1), 2000);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center px-6 transition-colors ${isDark ? '' : 'bg-[#F8FAFC]'}`} data-testid="payment-success-page">
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: `radial-gradient(circle, ${status === 'success' ? 'rgba(16,185,129,0.1)' : isDark ? 'rgba(59,130,246,0.06)' : 'rgba(59,130,246,0.03)'} 0%, transparent 70%)` }} />

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center max-w-md relative"
      >
        {status === 'loading' && (
          <>
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}>
              <Loader2 className="w-16 h-16 text-blue-400 mx-auto mb-6" />
            </motion.div>
            <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit' }}>Processing Payment...</h1>
            <p className="text-white/40">Please wait while we confirm your payment</p>
          </>
        )}

        {status === 'success' && (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200 }}
            >
              <CheckCircle className="w-20 h-20 text-emerald-400 mx-auto mb-6" />
            </motion.div>
            <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit' }}>Payment Successful!</h1>
            <p className="text-white/40 mb-2">Your appeal has been submitted and is being processed.</p>
            {paymentData && (
              <p className="text-sm text-white/30 mb-8">
                Amount: {paymentData.currency?.toUpperCase()} {(paymentData.amount_total / 100).toFixed(2)}
              </p>
            )}
            <div className="flex gap-3 justify-center">
              <Button onClick={() => navigate('/dashboard')} className="bg-blue-500 hover:bg-blue-600 rounded-xl" data-testid="go-to-dashboard-btn">
                Go to Dashboard
              </Button>
              <Button variant="outline" onClick={() => navigate('/')} className="border-white/[0.08] text-white/60 hover:text-white rounded-xl" data-testid="back-to-home-btn">
                Back to Home
              </Button>
            </div>
          </>
        )}

        {(status === 'expired' || status === 'timeout') && (
          <>
            <XCircle className="w-20 h-20 text-red-400 mx-auto mb-6" />
            <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit' }}>Payment Issue</h1>
            <p className="text-white/40 mb-8">
              {status === 'expired' ? 'Your payment session has expired.' : 'Unable to confirm payment. Please check your email for confirmation.'}
            </p>
            <Button onClick={() => navigate('/')} className="bg-blue-500 hover:bg-blue-600 rounded-xl">
              Try Again
            </Button>
          </>
        )}
      </motion.div>
    </div>
  );
}
